import React from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { StargraveTechnology, StargraveTechnologyInstance } from '../../types';
import { formatStargraveBuffEffect, getStargraveBuffById } from '../../game/stargraveBuffRules';

interface UseTechnologyDialogProps {
  instance: StargraveTechnologyInstance;
  technology: StargraveTechnology;
  members: Array<{ id: string; name: string }>;
  onClose: () => void;
  onConfirm: (options?: { healTargetId?: string; healAmount?: number }) => void;
}

/** Confirms using an Advanced Technology item, applying its buff and/or healing. */
export const UseTechnologyDialog: React.FC<UseTechnologyDialogProps> = ({
  instance,
  technology,
  members,
  onClose,
  onConfirm,
}) => {
  const buff = technology.useBuffId ? getStargraveBuffById(technology.useBuffId) : undefined;
  const ownerName = members.find((member) => member.id === instance.ownerId)?.name;
  const isHeal = technology.healAmount !== undefined;
  const [healTargetId, setHealTargetId] = React.useState(instance.ownerId ?? members[0]?.id ?? '');
  const [healAmount, setHealAmount] = React.useState(technology.healAmount ?? 0);

  const missingOwner = Boolean(buff) && !instance.ownerId;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Use {technology.name}</DialogTitle>
      <DialogContent>
        {buff && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {missingOwner
              ? 'Assign an owner before using this item.'
              : `This will apply ${buff.name} (${formatStargraveBuffEffect(buff)}) to ${ownerName ?? 'the owner'}.`}
          </Typography>
        )}
        {!buff && !isHeal && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            No automated effect — apply the effect manually at the table.
          </Typography>
        )}
        {isHeal && (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Restores up to {technology.healAmount} Health. Choose the target and adjust the amount if needed.
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id={`heal-target-${instance.instanceId}`}>Target</InputLabel>
              <Select
                labelId={`heal-target-${instance.instanceId}`}
                label="Target"
                value={healTargetId}
                onChange={(event) => setHealTargetId(event.target.value)}
              >
                {members.map((member) => <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Health restored"
              type="number"
              value={healAmount}
              onChange={(event) => setHealAmount(Math.max(0, parseInt(event.target.value, 10) || 0))}
              fullWidth
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </>
        )}
        {instance.remainingUses !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Uses remaining: {instance.remainingUses}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={missingOwner || (isHeal && !healTargetId)}
          onClick={() => onConfirm(isHeal ? { healTargetId, healAmount } : undefined)}
        >
          Use
        </Button>
      </DialogActions>
    </Dialog>
  );
};
